import React, { useState, useRef, useEffect } from 'react';
import { 
  X, ExternalLink, ChevronLeft, ChevronRight, 
  TrendingUp, Users, ShoppingCart, Calendar,
  Quote, ArrowUpRight, Layers, Briefcase
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Categories mapping for display
const categoryLabels = {
  'all': 'Tous',
  'Site vitrine': 'Site vitrine',
  'E-commerce': 'E-commerce',
  'Application web': 'Application web',
  'Refonte': 'Refonte',
  'Landing page': 'Landing page',
  'SEO': 'SEO'
};

// Before/After Slider Component
const BeforeAfterSlider = ({ before, after, title }) => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const containerRef = useRef(null);
  const isDragging = useRef(false);

  const handleMove = (clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  };

  const handleMouseDown = () => {
    isDragging.current = true;
  };

  const handleMouseUp = () => {
    isDragging.current = false;
  };

  const handleMouseMove = (e) => {
    if (isDragging.current) {
      handleMove(e.clientX);
    }
  };

  const handleTouchMove = (e) => {
    handleMove(e.touches[0].clientX);
  };

  useEffect(() => {
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mousemove', handleMouseMove);
    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  return (
    <div 
      ref={containerRef}
      className="relative w-full aspect-video overflow-hidden cursor-ew-resize select-none"
      onMouseDown={handleMouseDown}
      onTouchMove={handleTouchMove}
    >
      {/* After Image (Background) */}
      <img 
        src={after} 
        alt={`${title} - Après`}
        className="absolute inset-0 w-full h-full object-cover"
      />
      
      {/* Before Image (Clipped) */}
      <div 
        className="absolute inset-0 overflow-hidden"
        style={{ width: `${sliderPosition}%` }}
      >
        <img 
          src={before} 
          alt={`${title} - Avant`}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ width: `${containerRef.current?.offsetWidth || 100}px` }}
        />
      </div>

      {/* Slider Handle */}
      <div 
        className="absolute top-0 bottom-0 w-1 bg-accent cursor-ew-resize z-10"
        style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
      >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-accent rounded-full flex items-center justify-center shadow-lg">
          <ChevronLeft size={14} className="text-black -mr-1" />
          <ChevronRight size={14} className="text-black -ml-1" />
        </div>
      </div>

      {/* Labels */}
      <div className="absolute top-4 left-4 px-3 py-1 bg-black/70 text-white text-xs font-medium backdrop-blur-sm">
        AVANT
      </div>
      <div className="absolute top-4 right-4 px-3 py-1 bg-accent text-black text-xs font-medium">
        APRÈS
      </div>
    </div>
  );
};

// Project Modal Component
const ProjectModal = ({ project, isOpen, onClose }) => {
  if (!isOpen || !project) return null;

  const hasBeforeAfter = project.image_before && project.image_after;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/90 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal Content */}
      <div className="relative bg-[#0a0a0a] border border-white/10 max-w-5xl w-full max-h-[90vh] overflow-y-auto animate-fadeIn">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-10 h-10 bg-white/10 hover:bg-accent hover:text-black text-white flex items-center justify-center transition-all duration-300"
        >
          <X size={20} />
        </button>

        {/* Image Section */}
        {hasBeforeAfter ? (
          <BeforeAfterSlider 
            before={project.image_before} 
            after={project.image_after} 
            title={project.title}
          />
        ) : (project.image_main || project.image_after) ? (
          <div className="relative aspect-video">
            <img 
              src={project.image_main || project.image_after} 
              alt={project.title}
              className="w-full h-full object-cover"
            />
          </div>
        ) : (
          <div className="aspect-video bg-gradient-to-br from-accent/20 to-violet-500/20 flex items-center justify-center">
            <Briefcase size={64} className="text-white/20" />
          </div>
        )}

        {/* Content */}
        <div className="p-8">
          {/* Header */}
          <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
            <div>
              <span className="text-accent text-sm font-medium uppercase tracking-wider">
                {project.category}
              </span>
              <h2 className="text-3xl font-semibold text-white mt-2">{project.title}</h2>
              {project.client_name && (
                <p className="text-white/60 mt-1">Client: {project.client_name}</p>
              )}
            </div>
            {project.website_url && (
              <a 
                href={project.website_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-5 py-2 bg-accent text-black font-medium hover:bg-accent/80 transition-colors"
              >
                Voir le site
                <ExternalLink size={16} />
              </a>
            )}
          </div>

          {/* Meta Info */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {project.client_name && (
              <div className="bg-white/[0.03] p-4">
                <div className="text-white/40 text-xs mb-1">Client</div>
                <div className="text-white font-medium">{project.client_name}</div>
              </div>
            )}
            {project.year && (
              <div className="bg-white/[0.03] p-4">
                <div className="text-white/40 text-xs mb-1">Année</div>
                <div className="text-white font-medium">{project.year}</div>
              </div>
            )}
            {project.results && (
              <div className="bg-white/[0.03] p-4 col-span-2">
                <div className="text-white/40 text-xs mb-1">Résultats</div>
                <div className="text-accent font-medium">{project.results}</div>
              </div>
            )}
          </div>

          {/* Description */}
          <div className="mb-8">
            <h3 className="text-white font-semibold mb-3">À propos du projet</h3>
            <p className="text-white/60 leading-relaxed">{project.description}</p>
          </div>

          {/* Technologies */}
          {project.technologies && project.technologies.length > 0 && (
            <div className="mb-8">
              <h3 className="text-white font-semibold mb-3">Technologies utilisées</h3>
              <div className="flex flex-wrap gap-2">
                {project.technologies.map((tech, index) => (
                  <span 
                    key={index}
                    className="px-3 py-1 bg-accent/10 text-accent text-sm border border-accent/20"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Results Highlight */}
          {project.results && (
            <div className="bg-gradient-to-br from-accent/10 to-transparent border border-accent/20 p-6 text-center">
              <TrendingUp className="w-8 h-8 text-accent mx-auto mb-3" />
              <div className="text-2xl font-bold text-accent mb-1">{project.results}</div>
              <div className="text-white/50 text-sm">Résultats obtenus</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Main Portfolio Section
const PortfolioSection = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedProject, setSelectedProject] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch projects from API
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const res = await fetch(`${API_URL}/api/portfolio/projects`);
        if (res.ok) {
          const data = await res.json();
          setProjects(data.projects || []);
          
          // Extract unique categories from projects
          const uniqueCategories = [...new Set(data.projects.map(p => p.category))];
          setCategories(['all', ...uniqueCategories]);
        }
      } catch (error) {
        console.error('Error fetching portfolio:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchProjects();
  }, []);

  const filteredProjects = activeFilter === 'all' 
    ? projects 
    : projects.filter(p => p.category === activeFilter);

  const openProject = (project) => {
    setSelectedProject(project);
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setIsModalOpen(false);
    document.body.style.overflow = 'auto';
  };

  // Don't render section if no projects
  if (!loading && projects.length === 0) {
    return null;
  }

  return (
    <section id="portfolio" className="relative bg-[#050505] py-32 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-grid-pattern opacity-[0.02]" />
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-accent/5 rounded-full blur-[200px]" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-violet-500/5 rounded-full blur-[200px]" />

      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-accent text-sm font-medium tracking-wider uppercase mb-4 block">
            Portfolio
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-white mb-6">
            Nos <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-violet-400">réalisations</span>
          </h2>
          <p className="text-white/50 text-lg max-w-2xl mx-auto">
            Découvrez nos projets récents et les résultats exceptionnels obtenus pour nos clients.
          </p>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-12 h-12 border-2 border-accent/30 border-t-accent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Filters */}
            {categories.length > 2 && (
              <div className="flex flex-wrap justify-center gap-3 mb-12">
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => setActiveFilter(category)}
                    className={`px-6 py-2 text-sm font-medium transition-all duration-300 ${
                      activeFilter === category
                        ? 'bg-accent text-black'
                        : 'bg-white/[0.03] text-white/60 border border-white/[0.05] hover:border-white/[0.15] hover:text-white'
                    }`}
                  >
                    {categoryLabels[category] || category}
                  </button>
                ))}
              </div>
            )}

            {/* Projects Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredProjects.map((project, index) => (
                <div
                  key={project.id}
                  className="group relative bg-white/[0.02] border border-white/[0.05] overflow-hidden cursor-pointer hover:border-accent/30 transition-all duration-500"
                  onClick={() => openProject(project)}
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  {/* Image */}
                  <div className="relative aspect-[4/3] overflow-hidden">
                    {(project.image_main || project.image_after) ? (
                      <img 
                        src={project.image_main || project.image_after} 
                        alt={project.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-accent/20 to-violet-500/20 flex items-center justify-center">
                        <Briefcase size={48} className="text-white/20" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                    
                    {/* Category Badge */}
                    <div className="absolute top-4 left-4 px-3 py-1 bg-accent/90 text-black text-xs font-medium">
                      {project.category}
                    </div>

                    {/* Featured Badge */}
                    {project.featured && (
                      <div className="absolute top-4 right-4 px-2 py-1 bg-white/10 backdrop-blur-sm text-white text-xs font-medium">
                        ⭐ À la une
                      </div>
                    )}

                    {/* View Project Overlay */}
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/50">
                      <span className="flex items-center gap-2 px-6 py-3 bg-accent text-black font-medium">
                        Voir le projet
                        <ArrowUpRight size={18} />
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-white mb-1 group-hover:text-accent transition-colors duration-300">
                      {project.title}
                    </h3>
                    <p className="text-white/50 text-sm mb-4 line-clamp-2">
                      {project.description}
                    </p>

                    {/* Results Preview */}
                    <div className="flex items-center gap-4 text-sm">
                      {project.results && (
                        <div className="flex items-center gap-1 text-accent">
                          <TrendingUp size={14} />
                          <span className="truncate max-w-[150px]">{project.results}</span>
                        </div>
                      )}
                      {project.year && (
                        <>
                          <div className="w-px h-4 bg-white/10" />
                          <div className="flex items-center gap-1 text-white/50">
                            <Calendar size={14} />
                            <span>{project.year}</span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Bottom Accent Line */}
                  <div className="absolute bottom-0 left-0 w-0 h-px bg-accent group-hover:w-full transition-all duration-500" />
                </div>
              ))}
            </div>
          </>
        )}

        {/* CTA */}
        <div className="text-center mt-16">
          <p className="text-white/40 text-sm mb-4">
            Vous avez un projet similaire en tête ?
          </p>
          <a
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="inline-flex items-center gap-2 px-8 py-4 bg-accent text-black font-medium hover:bg-accent/10 hover:text-accent transition-all duration-400"
          >
            <Layers size={18} />
            Discuter de votre projet
          </a>
        </div>
      </div>

      {/* Project Modal */}
      <ProjectModal 
        project={selectedProject} 
        isOpen={isModalOpen} 
        onClose={closeModal} 
      />
    </section>
  );
};

export default PortfolioSection;
